import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  NgZone,
  OnInit,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
// environment import removed because it's unused in this component
import moment from 'moment';
import {
  BrandModels,
  Labels,
  production,
  ProductionHistory
} from 'src/app/Models/ApplicationModels';
import { QzTrayService } from 'src/app/qz-tray.service';
import { CustomToastsService } from 'src/app/Services/custom-toasts.service';
import { MasterDataSeriveService } from 'src/app/Services/master-data-serive.service';
import { UserserviceService } from 'src/app/Services/userservice.service';
import { BreadcrumbService } from '../../../Services/breadcrumb.service';
import {
  ChatMessage,
  CommandHandler,
  Department,
  DepartmentData,
  DropdownOption,
  LLMIntentResponse,
  ProductInfo
} from '../models/chatbot-models';
import { ProductionChatbotApiService } from '../services/production-chatbot-api.service';
import { LabelEntityType } from 'src/app/Services/enum';
import { NgxPermissionsService } from 'ngx-permissions';
import { firstValueFrom } from 'rxjs';
import { InfoQueryMatch } from '../models/chatbot-knowledge';
import { ChatbotQueryService } from '../services/chatbot-query.service';

const LOGO_PATH = 'assets/icons/bot.svg';
const USER_IMAGE_PATH = 'assets/icons/User profile.png';

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.css']
})
export class ChatbotComponent implements OnInit {
  readonly logoPath = LOGO_PATH;
  readonly userImagePath = USER_IMAGE_PATH;
  private readonly platformId = inject(PLATFORM_ID);
  private readonly ngZone = inject(NgZone);
  private readonly breadcrumbService = inject(BreadcrumbService);
  readonly isBrowser = isPlatformBrowser(this.platformId);

  @ViewChild('camera') cameraRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('chatScroll') private chatScrollContainer!: ElementRef;

  permissionMessage: string | null = null;
  stream: MediaStream | null = null;
  mediaRecorder: MediaRecorder | null = null;
  recordedChunks: BlobPart[] = [];
  isRecording = false;
  isPaused = false;
  playbackUrl: string | null = null;
  isProcessing = false;
  isUploading = false;
  isListening = false;
  recognition: any = null;
  productionItem: production = new production();
  ProductInfo: ProductInfo = {} as ProductInfo;
  productionCycleId: number = 0;
  productionCycleScheduleId: number = 0;
  quantity: any = 1;
  pieces: any = 1;
  printHistory: Array<ProductionHistory> = [];
  printerType: number = 0;
  formatLabels: Array<Labels> = [];
  previewZplCode: string = '';
  previousMessageCount = 0;
  overrideDetails: any;
  isOverride: boolean = false;

  chatMessages: ChatMessage[] = [
    { sender: 'bot', text: 'Hi, how would you like to proceed?' }
  ];

  inputText = '';

  hierarchicalData: Department[] = [];
  dropdownData: DepartmentData = {
    departments: [],
    categories: {}
  };

  qualityOptions: DropdownOption[] = [];

  sizeOptions: { [key: string]: { [key: string]: DropdownOption[] } } = {};
  priceOptions: {
    [key: string]: { [key: string]: { [key: string]: DropdownOption[] } };
  } = {};

  brands: Array<BrandModels> = [];
  brandsForSearch: Array<BrandModels> = [];
  brandsDescrInEnglish: string[] = [];

  private chatCommands: Record<string, CommandHandler> = {
    'update department': this.handleUpdateDepartment.bind(this),
    'update category': this.handleUpdateCategory.bind(this),
    'update quality': this.handleUpdateQuality.bind(this),
    'update price': this.handleUpdatePrice.bind(this),
    'update subcategory': this.handleUpdateSubcategory.bind(this),
    'update size': this.handleUpdateSize.bind(this),
    'show departments': () => this.showAvailableDepartments(),
    'show department': () => this.showAvailableDepartments(),
    'show categories': () => this.showAvailableCategories(),
    'show category': () => this.showAvailableCategories(),
    'show hierarchy': () => this.displayDepartmentHierarchy()
  };

  // Quick-access questions shown above the input, already filtered to what this
  // user is allowed to ask.
  suggestionChips: Array<{ label: string; query: string }> = [];

  constructor(
    private masterService: MasterDataSeriveService,
    private userService: UserserviceService,
    private productionApi: ProductionChatbotApiService,
    private toastr: CustomToastsService,
    private printService: QzTrayService,
    private permissionsService: NgxPermissionsService,
    private queryService: ChatbotQueryService
  ) {}

  private grantedPermissions(): string[] {
    return Object.keys(this.permissionsService.getPermissions() || {});
  }

  private buildSuggestionChips(): void {
    const granted = this.grantedPermissions();
    this.suggestionChips = [
      ...this.queryService
        .infoQueriesFor(granted)
        // A lookup that needs a search term makes a poor one-tap chip, since
        // tapping it would send a question with nothing to look up.
        .filter((query) => !query.takesArgument)
        .map((query) => ({ label: query.question, query: query.question })),
      ...this.queryService
        .faqsFor(granted)
        .map((faq) => ({ label: faq.question, query: faq.question }))
    ];
  }

  useSuggestion(chip: { query: string }): void {
    this.submitCommand(chip.query);
  }

  async ngOnInit() {
    // Set breadcrumb to hide the header for chatbot
    this.isOverride = localStorage.getItem('isOverride') === 'true';
    this.overrideDetails = JSON.parse(
      localStorage.getItem('overrideDetails') || '{}'
    );
    this.breadcrumbService.updateActiveDeptTab('Home');
    this.breadcrumbService.updateActiveCategoryTab('AI Chatbot');
    this.printService.initQZ();
    this.buildSuggestionChips();
    this.fetchData();
  }
  ngAfterViewChecked() {
    if (this.chatMessages.length !== this.previousMessageCount) {
      this.scrollToBottom();
      this.previousMessageCount = this.chatMessages.length;
    }
  }

  fetchData() {
    let user = this.masterService.getUserDetailsFromStorage();
    let storeId = JSON.parse(localStorage.getItem('storeId')!);
    this.fetchHierarchicalData(user.companyID, storeId);
    this.fetchSizeData(user.companyID);
    this.fetchPricingConfig(user.companyID);
    this.getLabels();
    this.getProductionCycles();
  }

  private scrollToBottom(): void {
    try {
      if (this.chatScrollContainer) {
        this.chatScrollContainer.nativeElement.scrollTop =
          this.chatScrollContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.error('Scroll to bottom failed:', err);
    }
  }

  getProductionCycles() {
    let productionData = JSON.parse(localStorage.getItem('productionCycle')!);
    if (productionData && productionData.length > 0) {
      if (productionData[0].productionCycleId)
        this.productionCycleId = productionData[0].productionCycleId;
      if (productionData[0].productionCyclescheduleId)
        this.productionCycleScheduleId =
          productionData[0].productionCyclescheduleId;
    }
    if (this.productionCycleId == 0) this.productionCycleId = 1;
  }

  getLabels() {
    let userdata = this.masterService.getUserDetailsFromStorage();
    let c_Id = userdata.companyID;
    this.masterService.getAllLabels(c_Id).subscribe((res) => {
      const data: Labels[] = Array.isArray(res) ? res : [res];

      this.formatLabels = data.filter(
        (label) => label.labelEntityType === LabelEntityType.Product
      );

      this.formatLabels.map((res) => {
        if (res.id == this.ProductInfo.labelId) {
          this.printerType = res.printerType;
        }
      });
    });
  }

  async getBrands(deptID: number) {
    if (!deptID) {
      this.brands = [];
      this.brandsForSearch = [];
      this.brandsDescrInEnglish = [];
      return;
    }

    try {
      const res = await this.masterService
        .getBrandProduction(deptID)
        .toPromise();
      this.brands = res as BrandModels[];
      this.brands.sort((a, b) =>
        (a?.brandDescr ?? '').localeCompare(b?.brandDescr ?? '')
      );
      this.brandsForSearch = this.brands;
      this.brandsDescrInEnglish = this.brands.map(
        (brand: { brandDescr: any }) => brand.brandDescr
      );
      const brandsDescrToTranslate = this.brandsDescrInEnglish;
      this.translateBranddescr(brandsDescrToTranslate);
    } catch (error) {
      console.error('Error fetching brands:', error);
      this.brands = [];
      this.brandsForSearch = [];
      this.brandsDescrInEnglish = [];
    }
  }

  translateBranddescr(brandsDescrToTranslate: string[]) {
    // Translation logic can be implemented here if needed
    // For now, we'll keep the original brand descriptions
    console.log('Brand descriptions to translate:', brandsDescrToTranslate);
  }

  private async fetchHierarchicalData(companyId: number, storeId: number) {
    try {
      const apiResponse: { departments: Department[] } =
        await this.productionApi.getHierarchicalData(companyId, storeId);
      this.hierarchicalData = apiResponse.departments || [];

      this.dropdownData = {
        departments: this.hierarchicalData.map((dept) => ({
          value: dept.departmentName,
          label: dept.departmentName
        })),
        categories: {}
      };

      this.hierarchicalData.forEach((dept) => {
        this.dropdownData.categories[dept.departmentName] = dept.categories.map(
          (cat) => ({
            value: cat.categoryName,
            label: cat.categoryName
          })
        );
      });

      console.log('Hierarchical data loaded:', this.hierarchicalData);
    } catch (error) {
      console.error('Error fetching hierarchical data:', error);
      this.hierarchicalData = [];
      this.dropdownData = { departments: [], categories: {} };
      this.addBotMessage(
        'Failed to load department and category data. Please try again later.'
      );
    }
  }

  private async fetchSizeData(companyId: number) {
    try {
      // TPM's /SizePricing/sizes groups sizes by department/category with a nested
      // "sizing" array, rather than the flat per-size list the old proxy returned -
      // flatten it here into the same sizeOptions shape the chat UI expects.
      const groups: any[] = (await this.productionApi.getSizes(companyId)) || [];
      this.sizeOptions = {};
      groups.forEach((group) => {
        const dept = group.departmentDescr || 'Misc';
        const cat = group.categoryName || 'Uncategorized';
        if (!this.sizeOptions[dept]) this.sizeOptions[dept] = {};
        if (!this.sizeOptions[dept][cat]) this.sizeOptions[dept][cat] = [];
        (group.sizing || []).forEach((item: any) => {
          this.sizeOptions[dept][cat].push({
            value: item.alphaSize || item.numericSizeRange,
            label: item.alphaSize || item.numericSizeRange
          });
        });
      });

      console.log('Size data loaded:', this.sizeOptions);
    } catch (error) {
      console.error('Error fetching size data:', error);
      this.sizeOptions = {};
      this.addBotMessage('Failed to load size data. Please try again later.');
    }
  }

  private async fetchPricingConfig(companyId: number) {
    try {
      const configs: any[] = (await this.productionApi.getPricingConfig(companyId)) || [];
      this.priceOptions = {};

      const uniqueQualities = new Set<string>();

      configs.forEach((config) => {
        const dept = config.department || 'Misc';
        const cat = config.categoryName || 'Uncategorized';
        const sub = config.subCategoryName || 'None';

        if (!this.priceOptions[dept]) this.priceOptions[dept] = {};
        if (!this.priceOptions[dept][cat]) this.priceOptions[dept][cat] = {};
        if (!this.priceOptions[dept][cat][sub])
          this.priceOptions[dept][cat][sub] = [];

        this.priceOptions[dept][cat][sub].push({
          value: config.price.toString(),
          label: `$${config.price} (${config.qualityDescr})`
        });

        // Collect unique quality descriptions
        if (config.qualityDescr) {
          uniqueQualities.add(config.qualityDescr);
        }
      });

      // Populate qualityOptions from the unique qualities
      this.qualityOptions = Array.from(uniqueQualities)
        .sort()
        .map((quality) => ({
          value: quality,
          label: quality
        }));
    } catch (error) {
      console.error('Error fetching pricing config:', error);
      this.priceOptions = {};
      this.qualityOptions = [];
      this.addBotMessage(
        'Failed to load pricing data. Please try again later.'
      );
    }
  }

  getDepartmentOptions(): DropdownOption[] {
    return this.hierarchicalData.map((dept) => ({
      value: dept.departmentName,
      label: dept.departmentName
    }));
  }

  getCategoriesForDepartmentById(departmentId: number): DropdownOption[] {
    const department = this.hierarchicalData.find(
      (d) => d.departmentId === departmentId
    );
    return (
      department?.categories.map((cat) => ({
        value: cat.categoryName,
        label: cat.categoryName
      })) || []
    );
  }

  getSubcategoriesForCategory(
    departmentName: string,
    categoryName: string
  ): string[] {
    const department = this.hierarchicalData.find(
      (d) => d.departmentName === departmentName
    );
    const category = department?.categories.find(
      (c) => c.categoryName === categoryName
    );
    return category?.subCategories?.map((sub) => sub.subCategoryName) || [];
  }

  displayDepartmentHierarchy(): void {
    let hierarchyText = 'Department-wise Categories & Subcategories:\n\n';
    this.hierarchicalData.forEach((dept) => {
      hierarchyText += `**${dept.departmentName}** (ID: ${dept.departmentId})\n`;
      hierarchyText += `Categories (${dept.categories.length}):\n`;
      dept.categories.forEach((cat) => {
        hierarchyText += `  • ${cat.categoryName} (ID: ${cat.categoryId})\n`;
        if (cat.subCategories && cat.subCategories.length > 0) {
          hierarchyText += `    Subcategories:\n`;
          cat.subCategories.forEach((sub) => {
            hierarchyText += `      - ${sub.subCategoryName} (ID: ${sub.subCategoryId})\n`;
          });
        }
      });
      hierarchyText += '\n';
    });
    this.addBotMessage(hierarchyText);
  }

  private async tryGetMedia(withAudio: boolean): Promise<MediaStream> {
    const constraints: MediaStreamConstraints = {
      video: { facingMode: 'environment' },
      audio: withAudio
        ? {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        : false
    };
    return navigator.mediaDevices.getUserMedia(constraints);
  }

  async ensureCamera() {
    if (!this.isBrowser) return;
    if (this.stream) return;

    if (!(window.isSecureContext || location.hostname === 'localhost')) {
      this.permissionMessage =
        'Camera requires HTTPS. Please use a secure URL or localhost.';
      return;
    }

    try {
      this.stream = await this.tryGetMedia(true);
      this.permissionMessage = null;
    } catch (e: any) {
      if (e && (e.name === 'NotAllowedError' || e.name === 'SecurityError')) {
        try {
          this.stream = await this.tryGetMedia(false);
          this.permissionMessage =
            'Microphone was blocked. Recording video without audio.';
        } catch (e2: any) {
          console.error('Camera init error (video-only failed)', e2);
          this.permissionMessage =
            "Camera access is blocked. Click the camera icon in your browser's address bar to allow access, then try again.";
          return;
        }
      } else {
        console.error('Camera init error', e);
        this.permissionMessage =
          'Unable to access camera. Please grant permissions and try again.';
        return;
      }
    }

    if (this.cameraRef?.nativeElement && this.stream) {
      // Attach the stream to the video element but mute it so the local
      // microphone audio isn't played back to the speakers (which would
      // be picked up by the mic and cause an echo loop).
      const vid = this.cameraRef.nativeElement;
      vid.srcObject = this.stream;
      try {
        // Mute playback to avoid feedback. Also set playsInline for mobile.
        vid.muted = true;
        vid.volume = 0;
        // playsInline improves behavior on mobile browsers
        (vid as HTMLVideoElement).playsInline = true;
      } catch (e) {
        // ignore if properties are not present
      }

      await vid.play();
    }
  }

  async start() {
    if (!this.isBrowser) return;
    await this.ensureCamera();
    if (!this.stream) return;

    if (this.playbackUrl) {
      URL.revokeObjectURL(this.playbackUrl);
      this.playbackUrl = null;
    }

    const mimeTypes = [
      'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
      'video/mp4',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm'
    ];
    let mimeType = '';
    for (const t of mimeTypes) {
      if (MediaRecorder.isTypeSupported(t)) {
        mimeType = t;
        break;
      }
    }

    try {
      this.recordedChunks = [];
      this.mediaRecorder = new MediaRecorder(
        this.stream,
        mimeType ? { mimeType } : undefined
      );

      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          this.recordedChunks.push(e.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        const blobType = mimeType || 'video/webm';
        const blob = new Blob(this.recordedChunks, { type: blobType });
        this.playbackUrl = URL.createObjectURL(blob);
      };

      this.mediaRecorder.start(1000);
      this.isRecording = true;
      this.isPaused = false;
    } catch (e) {
      console.error('Recording start failed', e);
      alert('Recording could not be started.');
    }
  }

  async handleStartClick() {
    this.isProcessing = true;
    try {
      await this.start();
    } finally {
      this.isProcessing = false;
    }
  }

  togglePause() {
    if (!this.mediaRecorder) return;
    if (this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause();
      this.isPaused = true;
    } else if (this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume();
      this.isPaused = false;
    }
  }

  private pauseRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause();
      this.isPaused = true;
      this.addBotMessage('Pausing recording...');
    } else {
      this.addBotMessage('Nothing to pause. Start recording first.');
    }
  }

  private resumeRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume();
      this.isPaused = false;
      this.addBotMessage('Resuming recording...');
    } else if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.addBotMessage('Already recording.');
    } else {
      this.addBotMessage('Nothing to resume. Start recording first.');
    }
  }

  private snapshot(): string | null {
    const vid = this.cameraRef?.nativeElement;
    if (!vid || !vid.videoWidth || !vid.videoHeight) return null;
    const canvas = document.createElement('canvas');
    canvas.width = vid.videoWidth;
    canvas.height = vid.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(vid, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  }

  async generate() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    await this.ensureCamera();
    this.isRecording = false;
    this.isPaused = false;

    if (this.recordedChunks.length > 0) {
      const blobType = this.mediaRecorder?.mimeType || 'video/webm';
      const extension = blobType.includes('mp4') ? 'mp4' : 'webm';
      const blob = new Blob(this.recordedChunks, { type: blobType });

      const formData = new FormData();
      formData.append('file', blob, `recording.${extension}`);
      let user = this.masterService.getUserDetailsFromStorage();
      let storeId = JSON.parse(localStorage.getItem('storeId')!);
      // Send the hierarchical data already loaded on this page instead of letting the
      // backend fetch (or worse, disk-cache) it again per video.
      formData.append(
        'hierarchical_data_json',
        JSON.stringify({ departments: this.hierarchicalData })
      );
      if (storeId) {
        formData.append('client_store_id', String(storeId));
      }
      try {
        const data = await this.productionApi.uploadVideo(
          formData,
          user.companyID
        );
        console.log('data', data);
        if (data && typeof data === 'object') {
          const product: ProductInfo = {};
          if (data.department != null) product.department = data.department;
          if (data.category != null) product.category = data.category;
          if (data.subcategory != null) product.subcategory = data.subcategory;
          if (data.size != null) product.size = data.size;
          if (data.price != null) product.price = data.price;
          if (data.qualityDescr != null)
            product.qualityDescr = data.qualityDescr;
          if (data.brand != null) product.brand = data.brand;
          if (data.departmentId != null)
            product.departmentId = data.departmentId;
          if (data.categoryId != null) product.categoryId = data.categoryId;
          if (data.subCategoryId != null)
            product.subCategoryId = data.subCategoryId;
          if (data.priceId != null) product.priceId = data.priceId;
          if (data.labelId != null) product.labelId = data.labelId;
          if (data.qualityId != null) product.qualityId = data.qualityId;
          if (data.conditionId != null) product.conditionId = data.conditionId;
          if (data.brandId != null) product.brandId = data.brandId;

          this.ProductInfo = product;

          // Fetch brands if departmentId is available
          if (product.departmentId) {
            this.getBrands(product.departmentId);
          }

          if (Object.keys(product).length > 0) {
            this.chatMessages.push({
              sender: 'bot',
              product,
              approved: false,
              editing: false
            });
          } else {
            // Still attach an (empty) product object - findLatestProductMessage() requires
            // msg.product to be truthy for "update department to X" etc. to have something to
            // edit. Without this, the manual-entry commands the message below suggests would
            // silently fail with "No product information found to update."
            this.chatMessages.push({
              sender: 'bot',
              product,
              approved: false,
              editing: false,
              text:
                "Couldn't automatically detect any product details from that video. " +
                'You can enter them manually - try "update department to X", ' +
                '"update category to Y", "update price to Z", etc.'
            });
          }
        } else {
          this.chatMessages.push({
            sender: 'bot',
            text: 'Invalid response from API. Expected product data.'
          });
        }
      } catch (error: unknown) {
        let errorMessage = 'Unknown error occurred';
        if (error instanceof Error) errorMessage = error.message;
        else if (typeof error === 'string') errorMessage = error;
        else if (
          error !== null &&
          typeof error === 'object' &&
          'message' in error
        )
          errorMessage = (error as any).message || 'Unknown error occurred';

        this.chatMessages.push({
          sender: 'bot',
          text: `Failed to fetch fresh data or upload/process video: ${errorMessage}.`
        });
      }

      this.recordedChunks = [];
    } else {
      this.chatMessages.push({
        sender: 'bot',
        text: 'No video recorded. Click "Start", wait a few seconds, then "Generate".'
      });
    }
  }

  async handleGenerateClick() {
    this.isUploading = true;
    try {
      await this.generate();
    } finally {
      this.isUploading = false;
    }
  }

  send() {
    this.submitCommand(this.inputText);
  }

  private submitCommand(raw: string) {
    const original = (raw || '').trim();
    const normalized = original.toLowerCase();
    if (!normalized) return;

    this.chatMessages.push({ sender: 'user', text: original });
    this.processCommand(normalized);
    this.inputText = '';
  }

  private cleanCommandValue(raw: string): string {
    return (raw || '').replace(/^(to|to be|as|is|at|for)\s+/i, '').trim();
  }

  private async processCommand(command: string): Promise<void> {
    // Recording controls (robust phrase matching)
    const text = command.toLowerCase();
    const contains = (w: string) => text.includes(w);
    const hasAll = (...words: string[]) => words.every((w) => contains(w));

    if (
      hasAll('start', 'record') ||
      hasAll('start', 'recording') ||
      /^start$/.test(text)
    ) {
      this.addBotMessage('Starting recording...');
      await this.handleStartClick();
      return;
    }
    if (contains('pause') || contains('hold on') || contains('hold')) {
      this.pauseRecording();
      return;
    }
    if (contains('resume') || contains('continue')) {
      this.resumeRecording();
      return;
    }
    if (contains('generate') || hasAll('create', 'label')) {
      this.addBotMessage('Generating product data...');
      await this.handleGenerateClick();
      return;
    }

    // Simple, robust text parsing for common update patterns
    if (this.tryHandleSimpleUpdate(text)) {
      return;
    }

    // Questions are answered before reaching for the analysis service, both
    // because they are cheaper and because that service's natural language
    // endpoint is not deployed everywhere.
    if (await this.tryHandleQuestion(text)) {
      return;
    }

    try {
      const llmResponse = await this.parseIntentWithLLM(command);
      if (llmResponse && llmResponse.intent) {
        await this.executeIntent(llmResponse.intent, llmResponse.parameters);
        return;
      }
    } catch (error) {
      console.error('Command parsing failed:', error);
      // Do not spam user with parsing errors; continue to exact fallbacks
    }

    const latestProductMsg = this.findLatestProductMessage();

    if (command in this.chatCommands) {
      this.chatCommands[command](latestProductMsg);
      return;
    }

    if (command.startsWith('update department ')) {
      const value = this.cleanCommandValue(
        command.replace('update department ', '')
      );
      this.updateDepartmentWithValue(latestProductMsg, value);
      return;
    }

    if (command.startsWith('update category ')) {
      const value = this.cleanCommandValue(
        command.replace('update category ', '')
      );
      this.updateCategoryWithValue(latestProductMsg, value);
      return;
    }

    if (command.startsWith('update quality ')) {
      const value = this.cleanCommandValue(
        command.replace('update quality ', '')
      );
      this.updateQualityWithValue(latestProductMsg, value);
      return;
    }

    if (command.startsWith('update price ')) {
      const value = this.cleanCommandValue(
        command.replace('update price ', '')
      );
      this.updatePriceWithValue(latestProductMsg, value);
      return;
    }

    if (command.startsWith('update subcategory ')) {
      const value = this.cleanCommandValue(
        command.replace('update subcategory ', '')
      );
      this.updateSubcategoryWithValue(latestProductMsg, value);
      return;
    }

    if (command.startsWith('update size ')) {
      const value = this.cleanCommandValue(command.replace('update size ', ''));
      this.updateSizeWithValue(latestProductMsg, value);
      return;
    }

    this.addBotMessage(
      'I did not catch that. I can change the product on screen:\n' +
        '• "update department [name]"\n' +
        '• "update category [name]"\n' +
        '• "update quality [option]"\n' +
        '• "update price [amount]"\n' +
        '• "update size [size]"\n' +
        '• "show departments" to see all departments\n' +
        '• "show categories" to see available categories\n' +
        '• "show hierarchy" to see the complete structure\n\n' +
        'I can also answer questions - type "help" to see what I can be asked.'
    );
  }

  private tryHandleSimpleUpdate(text: string): boolean {
    const latest = this.findLatestProductMessage();

    // price: "update price to $10", "set price $10", "price is $10"
    const priceMatch = text.match(
      /(?:update|set|change)\s+(?:the\s+)?price\s+(?:to\s+|at\s+|is\s+)?(.+)$|(?:^|\s)price\s+(?:to|is|at)\s+(.+)$/i
    );
    if (priceMatch) {
      const v = this.cleanCommandValue(
        (priceMatch[1] || priceMatch[2] || '').trim().replace(/[.!?]+$/, '')
      );
      this.updatePriceWithValue(latest, v);
      return true;
    }

    // department/category/quality/subcategory/size generic updates
    const generic = [
      {
        key: 'department',
        fn: (val: string) => this.updateDepartmentWithValue(latest, val)
      },
      {
        key: 'category',
        fn: (val: string) => this.updateCategoryWithValue(latest, val)
      },
      {
        key: 'quality',
        fn: (val: string) => this.updateQualityWithValue(latest, val)
      },
      {
        key: 'subcategory',
        fn: (val: string) => this.updateSubcategoryWithValue(latest, val)
      },
      {
        key: 'size',
        fn: (val: string) => this.updateSizeWithValue(latest, val)
      }
    ];

    for (const g of generic) {
      const re = new RegExp(
        `(?:update|set|change)\\s+(?:the\\s+)?${g.key}\\s+(?:to\\s+|as\\s+|is\\s+|at\\s+)?(.+)$`,
        'i'
      );
      const m = text.match(re);
      if (m) {
        const v = this.cleanCommandValue(
          (m[1] || '').trim().replace(/[.!?]+$/, '')
        );
        g.fn(v);
        return true;
      }
    }

    // show commands
    if (/show\s+departments?/i.test(text)) {
      this.showAvailableDepartments();
      return true;
    }
    if (/show\s+categories?/i.test(text)) {
      this.showAvailableCategories();
      return true;
    }
    if (/show\s+hierarchy/i.test(text)) {
      this.displayDepartmentHierarchy();
      return true;
    }

    return false;
  }

  // Returns true when the text was a question this assistant could answer, so
  // the caller stops looking for an edit command to run.
  private async tryHandleQuestion(text: string): Promise<boolean> {
    const granted = this.grantedPermissions();

    if (/^\s*(help|what can you do|commands?|faq)\s*[?.!]*\s*$/i.test(text)) {
      this.showHelp();
      return true;
    }

    const match = this.queryService.match(text, granted);
    if (!match) {
      return false;
    }

    if (match.kind === 'faq') {
      this.addBotMessage(match.entry.answer);
      return true;
    }

    await this.answerInfoQuery(match.query);
    return true;
  }

  private showHelp(): void {
    const granted = this.grantedPermissions();
    const questions = [
      ...this.queryService
        .infoQueriesFor(granted)
        .map((query) => query.question),
      ...this.queryService.faqsFor(granted).map((faq) => faq.question)
    ];

    this.addBotMessage(
      'You can ask me things like:\n' +
        questions.map((question) => `• ${question}`).join('\n') +
        '\n\nOr change the product on screen with commands like "update category jeans", "update size large" or "update price 12.99".'
    );
  }

  private async answerInfoQuery(match: InfoQueryMatch): Promise<void> {
    switch (match.kind) {
      case 'production-count':
        await this.answerProductionCount();
        return;
      case 'product-details':
        this.answerProductDetails();
        return;
      case 'current-license-plate':
        this.answerCurrentLicensePlate();
        return;
      case 'license-plate-search':
        await this.answerLicensePlateSearch(match.argument);
        return;
      case 'open-overrides':
        await this.answerOpenOverrides();
        return;
    }
  }

  private async answerProductionCount(): Promise<void> {
    const user = this.masterService.getUserDetailsFromStorage();
    try {
      // Named for the company but keyed on the user, matching how the header
      // counter calls it - the count is this user's own for today.
      const quantity: any = await firstValueFrom(
        this.masterService.getProductionQuantity(user.userId)
      );
      const count = Number(quantity ?? 0);
      this.addBotMessage(
        count === 1
          ? 'You have produced 1 item today.'
          : `You have produced ${count} items today.`
      );
    } catch (error) {
      console.error('Error fetching production count:', error);
      this.addBotMessage(
        'I could not reach TPM for your production count just now. Please try again in a moment.'
      );
    }
  }

  private answerProductDetails(): void {
    const message = this.findLatestProductMessage();
    const product = message?.product;
    if (!product) {
      this.addBotMessage(
        'There is no product on screen yet. Say "start recording", then "generate", and I will identify one.'
      );
      return;
    }

    const lines: string[] = [];
    const add = (label: string, value: unknown) => {
      if (value !== undefined && value !== null && `${value}`.trim() !== '') {
        lines.push(`• ${label}: ${value}`);
      }
    };

    add('Department', product.department);
    add('Category', product.category);
    add('Subcategory', product.subcategory);
    add('Brand', product.brand);
    add('Size', product.size);
    add('Quality', product.qualityDescr);
    add('Price', product.price ? `$${product.price}` : undefined);

    this.addBotMessage(
      lines.length > 0
        ? `Here is the product currently on screen:\n${lines.join('\n')}`
        : 'There is a product card on screen, but nothing has been identified on it yet.'
    );
  }

  private answerCurrentLicensePlate(): void {
    // this.isOverride/this.overrideDetails are only set in ngOnInit, but the
    // override toggle lives in the persistent nav menu outside the
    // router-outlet, so it can change without recreating this component.
    // Re-read localStorage here rather than trusting the cached fields.
    const isOverride = localStorage.getItem('isOverride') === 'true';
    const overrideDetails = JSON.parse(
      localStorage.getItem('overrideDetails') || '{}'
    );

    if (!isOverride || !overrideDetails?.licensePlateId) {
      this.addBotMessage(
        'You are not working under an override at the moment, so items you produce are not being added to a license plate. Ask "how do I override a license plate?" to set one up.'
      );
      return;
    }

    const lines = [
      `• License plate: ${
        overrideDetails.licensePlateDesc || overrideDetails.licensePlateId
      }`
    ];
    if (overrideDetails.overrideDesc) {
      lines.push(`• Override: ${overrideDetails.overrideDesc}`);
    }
    if (overrideDetails.weekColorName) {
      lines.push(`• Week colour: ${overrideDetails.weekColorName}`);
    }
    if (overrideDetails.date) {
      lines.push(`• Date: ${overrideDetails.date}`);
    }

    this.addBotMessage(
      `Everything you produce here is being added to:\n${lines.join('\n')}`
    );
  }

  // With no search term this lists what is there, which is what "show all
  // license plates" asks for; with one it narrows to the match.
  private async answerLicensePlateSearch(argument?: string): Promise<void> {
    const user = this.masterService.getUserDetailsFromStorage();
    try {
      const response: any = await firstValueFrom(
        this.masterService.getAllLicensePlates(
          user.companyID,
          undefined,
          argument
        )
      );
      const plates: any[] = Array.isArray(response)
        ? response
        : response?.data ?? [];

      if (plates.length === 0) {
        this.addBotMessage(
          argument
            ? `I could not find a license plate matching "${argument}".`
            : 'There are no license plates set up for your company yet.'
        );
        return;
      }

      const shown = plates.slice(0, 5);
      const lines = shown.map((plate) => {
        const parts = [
          plate.licensePlateDesc ||
            plate.description ||
            `Plate ${plate.licensePlateId ?? ''}`.trim()
        ];
        if (plate.locationName || plate.location) {
          parts.push(`at ${plate.locationName || plate.location}`);
        }
        if (plate.statusName || plate.status) {
          parts.push(`(${plate.statusName || plate.status})`);
        }
        return `• ${parts.join(' ')}`;
      });

      const more =
        plates.length > shown.length
          ? `\n...and ${plates.length - shown.length} more. Name one to narrow it down, for example "find license plate red bin".`
          : '';

      const heading = argument
        ? `License plates matching "${argument}":`
        : `License plates for your company (${plates.length}):`;

      this.addBotMessage(`${heading}\n${lines.join('\n')}${more}`);
    } catch (error) {
      console.error('Error searching license plates:', error);
      this.addBotMessage(
        'I could not reach TPM to search license plates just now. Please try again in a moment.'
      );
    }
  }

  private async answerOpenOverrides(): Promise<void> {
    const user = this.masterService.getUserDetailsFromStorage();
    const storeId = JSON.parse(localStorage.getItem('storeId') || 'null');
    try {
      const response: any = await firstValueFrom(
        this.masterService.getProductionOverrides(user.companyID, {
          sourceStoreId: storeId ?? undefined,
          scope: 'Current'
        })
      );
      const overrides: any[] = Array.isArray(response)
        ? response
        : response?.data ?? [];

      if (overrides.length === 0) {
        this.addBotMessage('There are no overrides open at the moment.');
        return;
      }

      const shown = overrides.slice(0, 5);
      const lines = shown.map((override) => {
        const parts = [
          override.overrideDesc ||
            override.description ||
            `Override ${override.overrideId ?? ''}`.trim()
        ];
        if (override.weekColorName) {
          parts.push(`- ${override.weekColorName}`);
        }
        const plateCount = override.licensePlates?.length;
        if (plateCount) {
          parts.push(`(${plateCount} license plate${plateCount === 1 ? '' : 's'})`);
        }
        return `• ${parts.join(' ')}`;
      });

      const more =
        overrides.length > shown.length
          ? `\n...and ${overrides.length - shown.length} more.`
          : '';

      this.addBotMessage(
        `Overrides currently open:\n${lines.join('\n')}${more}\nThey are set up on the Override screen in the production menu.`
      );
    } catch (error) {
      console.error('Error fetching overrides:', error);
      this.addBotMessage(
        'I could not reach TPM for the open overrides just now. Please try again in a moment.'
      );
    }
  }

  private async parseIntentWithLLM(
    userInput: string
  ): Promise<LLMIntentResponse | null> {
    try {
      const response = await this.productionApi.parseCommand(userInput, {
        departments: this.hierarchicalData
      });

      console.log('Parse command response:', response);

      if (response && response.data && response.data.intent) {
        if (response.status === 'success') {
          return {
            intent: response.data.intent,
            parameters: response.data.parameters?.value
              ? { value: response.data.parameters.value }
              : {}
          };
        } else if (response.status === 'error') {
          const errorMessage = response.message || 'Failed to parse command';
          const availableDepartments =
            response.data?.available_departments?.join(', ') || 'unknown';
          this.addBotMessage(
            `${errorMessage}. Available departments: ${availableDepartments}`
          );
          return null;
        }
      }

      console.error('Invalid parse-command response:', response);
      return null;
    } catch (error) {
      console.error('Parse command API error:', error);
      return null;
    }
  }

  private async executeIntent(
    intent: string,
    parameters?: { [key: string]: string }
  ): Promise<void> {
    const latestProductMsg = this.findLatestProductMessage();

    switch (intent) {
      case 'update_department':
        this.updateDepartmentWithValue(
          latestProductMsg,
          this.cleanCommandValue(parameters?.['value'] || '')
        );
        break;
      case 'update_category':
        this.updateCategoryWithValue(
          latestProductMsg,
          this.cleanCommandValue(parameters?.['value'] || '')
        );
        break;
      case 'update_quality':
        this.updateQualityWithValue(
          latestProductMsg,
          this.cleanCommandValue(parameters?.['value'] || '')
        );
        break;
      case 'update_price':
        this.updatePriceWithValue(
          latestProductMsg,
          this.cleanCommandValue(parameters?.['value'] || '')
        );
        break;
      case 'update_subcategory':
        this.updateSubcategoryWithValue(
          latestProductMsg,
          this.cleanCommandValue(parameters?.['value'] || '')
        );
        break;
      case 'update_size':
        this.updateSizeWithValue(
          latestProductMsg,
          this.cleanCommandValue(parameters?.['value'] || '')
        );
        break;
      case 'show_departments':
        this.showAvailableDepartments();
        break;
      case 'show_categories':
        this.showAvailableCategories();
        break;
      case 'show_hierarchy':
        this.displayDepartmentHierarchy();
        break;
      default:
        this.addBotMessage(
          'Sorry, I didn\'t recognize that command. Try something like "update department to media".'
        );
    }
  }

  private findLatestProductMessage(): ChatMessage | null {
    for (let i = this.chatMessages.length - 1; i >= 0; i--) {
      const msg = this.chatMessages[i];
      if (msg.sender === 'bot' && msg.product && !msg.approved) {
        return msg;
      }
    }
    return null;
  }

  private handleUpdateDepartment(productMsg: ChatMessage | null): void {
    if (!productMsg) {
      this.addBotMessage(
        'No product information found to update. Please generate a product first.'
      );
      return;
    }

    const departmentList = this.dropdownData.departments
      .map((dept) => `• ${dept.label}`)
      .join('\n');

    this.addBotMessage(
      `Which department would you like to update to?\n\nAvailable departments:\n${departmentList}\n\nType: "update department [name]"`
    );
  }

  private handleUpdateCategory(productMsg: ChatMessage | null): void {
    if (!productMsg) {
      this.addBotMessage(
        'No product information found to update. Please generate a product first.'
      );
      return;
    }

    if (!productMsg.product?.department) {
      this.addBotMessage(
        'Please set a department first before updating the category.'
      );
      return;
    }

    const categories = this.getCategoriesForDepartment(
      productMsg.product.department
    );
    const categoryList = categories.map((cat) => `• ${cat.label}`).join('\n');

    this.addBotMessage(
      `Available categories for ${productMsg.product.department}:\n${categoryList}\n\nType: "update category [name]"`
    );
  }

  private handleUpdateQuality(productMsg: ChatMessage | null): void {
    if (!productMsg) {
      this.addBotMessage(
        'No product information found to update. Please generate a product first.'
      );
      return;
    }

    const qualityList = this.qualityOptions
      .map((quality) => `• ${quality.label}`)
      .join('\n');

    this.addBotMessage(
      `Available quality options:\n${qualityList}\n\nType: "update quality [option]"`
    );
  }

  private handleUpdatePrice(productMsg: ChatMessage | null): void {
    if (!productMsg) {
      this.addBotMessage(
        'No product information found to update. Please generate a product first.'
      );
      return;
    }

    if (
      !productMsg.product?.department ||
      !productMsg.product?.category ||
      !productMsg.product?.subcategory
    ) {
      this.addBotMessage(
        'Please set department, category, and subcategory first before updating the price.'
      );
      return;
    }

    const dept = productMsg.product.department;
    const cat = productMsg.product.category;
    const sub = productMsg.product.subcategory || 'None';
    const prices = this.priceOptions[dept]?.[cat]?.[sub] || [];
    const priceList = prices.map((price) => `• ${price.label}`).join('\n');

    this.addBotMessage(
      `Available prices for ${dept} - ${cat} - ${sub}:\n${
        priceList || 'No prices available'
      }\n\nType: "update price [amount]"`
    );
  }

  private handleUpdateSubcategory(productMsg: ChatMessage | null): void {
    if (!productMsg) {
      this.addBotMessage(
        'No product information found to update. Please generate a product first.'
      );
      return;
    }

    if (!productMsg.product?.department || !productMsg.product?.category) {
      this.addBotMessage(
        'Please set department and category first before updating subcategory.'
      );
      return;
    }

    const subcategories = this.getSubcategoriesForCategory(
      productMsg.product.department,
      productMsg.product.category
    );

    if (subcategories.length > 0) {
      const subcategoryList = subcategories.map((sub) => `• ${sub}`).join('\n');
      this.addBotMessage(
        `Available subcategories:\n${subcategoryList}\n\nType: "update subcategory [name]"`
      );
    } else {
      this.addBotMessage(
        'No subcategories available for the selected category. You can enter a custom subcategory.\n\nType: "update subcategory [name]"'
      );
    }
  }

  private handleUpdateSize(productMsg: ChatMessage | null): void {
    if (!productMsg) {
      this.addBotMessage(
        'No product information found to update. Please generate a product first.'
      );
      return;
    }

    if (!productMsg.product?.department || !productMsg.product?.category) {
      this.addBotMessage(
        'Please set department and category first before updating size.'
      );
      return;
    }

    const dept = productMsg.product.department;
    const cat = productMsg.product.category;
    const sizes = this.sizeOptions[dept]?.[cat] || [];
    const sizeList = sizes.map((size) => `• ${size.label}`).join('\n');

    this.addBotMessage(
      `Available sizes for ${dept} - ${cat}:\n${
        sizeList || 'No sizes available'
      }\n\nType: "update size [size]"`
    );
  }

  private showAvailableDepartments(): void {
    const departmentList = this.dropdownData.departments
      .map((dept) => `• ${dept.value}: ${dept.label}`)
      .join('\n');

    this.addBotMessage(
      `Available departments:\n${departmentList || 'No departments available'}`
    );
  }

  private showAvailableCategories(): void {
    let allCategories = '';
    this.hierarchicalData.forEach((dept) => {
      allCategories += `${dept.departmentName}:\n`;
      dept.categories.forEach((cat) => {
        allCategories += `  • ${cat.categoryName}\n`;
        if (cat.subCategories && cat.subCategories.length > 0) {
          cat.subCategories.forEach((sub) => {
            allCategories += `    - ${sub.subCategoryName}\n`;
          });
        }
      });
      allCategories += '\n';
    });

    this.addBotMessage(
      `Available categories by department:\n\n${
        allCategories || 'No categories available'
      }`
    );
  }

  private updateDepartmentWithValue(
    productMsg: ChatMessage | null,
    value: string
  ): void {
    if (!productMsg || !productMsg.product) {
      this.addBotMessage(
        'No product information found to update. Please generate a product first.'
      );
      return;
    }

    console.log(
      'Updating department with value:',
      value,
      'Available departments:',
      this.dropdownData.departments
    );
    const department = this.dropdownData.departments.find(
      (dept) =>
        dept.label.toLowerCase() === value.toLowerCase() ||
        dept.value.toLowerCase() === value.toLowerCase()
    );

    if (department) {
      productMsg.product.department = department.value;
      if (productMsg.product.category) {
        productMsg.product.category = '';
        productMsg.product.subcategory = '';
        productMsg.product.size = '';
        // productMsg.product.price = '';
        this.addBotMessage(
          `✅ Department updated to "${department.label}". Category, subcategory, size, and price have been reset - please update them if needed.`
        );
      } else {
        this.addBotMessage(`✅ Department updated to "${department.label}".`);
      }
    } else {
      this.addBotMessage(
        `❌ Department "${value}" not found. Type "show departments" to see available options.`
      );
    }
  }

  private updateCategoryWithValue(
    productMsg: ChatMessage | null,
    value: string
  ): void {
    if (!productMsg || !productMsg.product) {
      this.addBotMessage(
        'No product information found to update. Please generate a product first.'
      );
      return;
    }

    if (!productMsg.product.department) {
      this.addBotMessage(
        '❌ Please set a department first before updating the category.'
      );
      return;
    }

    const availableCategories = this.getCategoriesForDepartment(
      productMsg.product.department
    );
    console.log(
      'Updating category with value:',
      value,
      'Available categories:',
      availableCategories
    );
    const category = availableCategories.find(
      (cat) =>
        cat.label.toLowerCase().includes(value.toLowerCase()) ||
        cat.value.toLowerCase().includes(value.toLowerCase())
    );

    if (category) {
      productMsg.product.category = category.value;
      productMsg.product.subcategory = '';
      productMsg.product.size = '';
      // productMsg.product.price = '';
      this.addBotMessage(
        `✅ Category updated to "${category.label}". Subcategory, size, and price have been reset.`
      );
    } else {
      this.addBotMessage(
        `❌ Category "${value}" not found for ${productMsg.product.department}. Type "update category" to see available options.`
      );
    }
  }

  private updateQualityWithValue(
    productMsg: ChatMessage | null,
    value: string
  ): void {
    if (!productMsg || !productMsg.product) {
      this.addBotMessage(
        'No product information found to update. Please generate a product first.'
      );
      return;
    }

    console.log(
      'Updating quality with value:',
      value,
      'Available qualities:',
      this.qualityOptions
    );
    const quality = this.qualityOptions.find(
      (qual) =>
        qual.label.toLowerCase().includes(value.toLowerCase()) ||
        qual.value.toLowerCase().includes(value.toLowerCase())
    );

    if (quality) {
      productMsg.product.qualityDescr = quality.value;
      this.addBotMessage(`✅ Quality updated to "${quality.label}".`);
    } else {
      this.addBotMessage(
        `❌ Quality "${value}" not found. Type "update quality" to see available options.`
      );
    }
  }

  private parseComplexPrice(input: string): number {
    input = input.replace(/\$/g, '').toLowerCase().trim();
    let dollars = 0,
      cents = 0;
    const dollarMatch = input.match(/(\d+(\.\d+)?)/);
    if (dollarMatch) {
      dollars = parseFloat(dollarMatch[1]);
    }
    const centsMatch = input.match(/(\d{1,2})\s*(c|cent|cents)/);
    if (centsMatch) {
      cents = parseInt(centsMatch[1]);
    }
    return parseFloat((dollars + cents / 100).toFixed(2));
  }

  private updatePriceWithValue(
    productMsg: ChatMessage | null,
    value: string
  ): void {
    if (!productMsg || !productMsg.product) {
      this.addBotMessage(
        'No product information found to update. Please generate a product first.'
      );
      return;
    }

    const numericValue = this.parseComplexPrice(value);
    if (isNaN(numericValue) || numericValue <= 0) {
      this.addBotMessage(
        'Please enter a valid price numeric value greater than 0.'
      );
      return;
    }

    productMsg.product.price = numericValue.toFixed(2);
    this.addBotMessage(`Price updated to ${productMsg.product.price}.`);
  }

  private updateSubcategoryWithValue(
    productMsg: ChatMessage | null,
    value: string
  ): void {
    if (!productMsg || !productMsg.product) {
      this.addBotMessage(
        'No product information found to update. Please generate a product first.'
      );
      return;
    }

    console.log(
      'Updating subcategory with value:',
      value,
      'Available subcategories:',
      this.getSubcategoriesForCategory(
        productMsg.product.department || '',
        productMsg.product.category || ''
      )
    );
    productMsg.product.subcategory = value;
    productMsg.product.size = '';
    // productMsg.product.price = '';
    this.addBotMessage(
      `✅ Subcategory updated to "${value}". Size and price have been reset.`
    );
  }

  private updateSizeWithValue(
    productMsg: ChatMessage | null,
    value: string
  ): void {
    if (!productMsg || !productMsg.product) {
      this.addBotMessage(
        'No product information found to update. Please generate a product first.'
      );
      return;
    }

    if (!productMsg.product?.department || !productMsg.product?.category) {
      this.addBotMessage(
        'Please set department and category first before updating size.'
      );
      return;
    }

    // If the value looks like a currency/price, treat it as a price update instead of size
    const currencyLike = /\$|\b(rs\.?|usd|dollars?)\b|\b\d+\s*c(ents?)?\b/i;
    if (currencyLike.test(value)) {
      this.updatePriceWithValue(productMsg, value);
      return;
    }

    const dept = productMsg.product.department;
    const cat = productMsg.product.category;
    const sizes = this.sizeOptions[dept]?.[cat] || [];
    console.log('Updating size with value:', value, 'Available sizes:', sizes);
    const size = sizes.find(
      (s) =>
        s.label.toLowerCase().includes(value.toLowerCase()) ||
        s.value.toLowerCase().includes(value.toLowerCase())
    );

    if (size) {
      productMsg.product.size = size.value;
      this.addBotMessage(`✅ Size updated to "${size.label}".`);
    } else {
      this.addBotMessage(`❌ Size "${value}" not found for ${dept} - ${cat}.`);
    }
  }

  private addBotMessage(text: string): void {
    setTimeout(() => {
      this.chatMessages.push({ sender: 'bot', text });
    }, 300);
  }

  micToggle() {
    if (!this.isBrowser) return;

    const SR: any =
      (window as any).webkitSpeechRecognition ||
      (window as any).SpeechRecognition;

    if (!SR) {
      this.addBotMessage(
        'Speech recognition is not supported in this browser.'
      );
      return;
    }

    if (this.isListening) {
      this.recognition.stop();
      this.isListening = false;
      return;
    }

    if (!this.recognition) {
      this.recognition = new SR();
      this.recognition.lang = 'en-US';
      this.recognition.interimResults = true;
      this.recognition.maxAlternatives = 1;

      this.recognition.onresult = (e: any) => {
        this.ngZone.run(() => {
          let interimTranscript = '';
          let finalTranscript = '';

          for (let i = 0; i < e.results.length; i++) {
            let transcript = e.results[i][0].transcript;
            transcript = transcript
              .replace(/[.!?]$/, '')
              .trim()
              .toLowerCase();
            if (e.results[i].isFinal) {
              finalTranscript += transcript;
            } else {
              interimTranscript += transcript;
            }
          }

          this.inputText = finalTranscript || interimTranscript;

          if (finalTranscript) {
            this.isListening = false;
            this.recognition.stop();

            // Submit the spoken text as a user message and process centrally
            if (this.inputText.trim()) {
              this.submitCommand(this.inputText);
            }
          }
        });
      };

      this.recognition.onend = () => {
        this.ngZone.run(() => {
          this.isListening = false;
        });
      };

      this.recognition.onerror = (e: any) => {
        this.ngZone.run(() => {
          console.error('Speech recognition error:', e.error);
          this.isListening = false;
          this.addBotMessage(
            `Speech recognition error: ${e.error}. Please try again.`
          );
        });
      };
    }

    try {
      this.recognition.start();
      this.isListening = true;
    } catch (error) {
      this.ngZone.run(() => {
        console.error('Failed to start speech recognition:', error);
        this.isListening = false;
        this.addBotMessage(
          'Failed to start speech recognition. Please try again.'
        );
      });
    }
  }

  approveProduct(msg: ChatMessage) {
    msg.approved = true;

    this.chatMessages.push({ sender: 'bot', text: 'Label generated.' });
    this.formatProductInfo();
  }

  formatProductInfo() {
    let user = this.masterService.getUserDetailsFromStorage();
    let storeId = JSON.parse(localStorage.getItem('tempStoreId')!);
    if (!storeId) {
      storeId = JSON.parse(localStorage.getItem('storeId')!);
    }
    this.productionItem.userID = user.userId;
    this.productionItem.formatTypeId = this.ProductInfo.labelId || 0;
    this.productionItem.isSpecialPrice = true;
    this.productionItem.sizeID = this.ProductInfo.sizeId || 0;
    this.productionItem.brandID = this.ProductInfo.brandId || 0;
    this.productionItem.CategoryID = this.ProductInfo.categoryId || 0;
    this.productionItem.subCategoryID = this.ProductInfo.subCategoryId || 0;
    this.productionItem.CategoryName = this.ProductInfo.category || '';
    this.productionItem.company = user.company;
    this.productionItem.isPrint = true;
    this.productionItem.DepartmentDescr = this.ProductInfo.department || '';
    this.productionItem.departmentId = this.ProductInfo.departmentId || 0;
    this.productionItem.sizeDescr = this.ProductInfo.size || '';
    this.productionItem.CompanyID = user.companyID;
    this.productionItem.productionStoreId = storeId;
    this.productionItem.price = Number(this.ProductInfo.price) || 0;
    this.productionItem.qualityID = this.ProductInfo.qualityId || 0;
    this.productionItem.conditionID = this.ProductInfo.conditionId || 0;
    this.productionItem.qty = parseFloat(this.quantity);
    this.productionItem.numberOfPieces = parseFloat(this.pieces);
    this.productionItem.productionCycleID = this.productionCycleId;
    this.productionItem.productionCycleScheduleID =
      this.productionCycleScheduleId;
    this.productionItem.addtoBin = false;
    this.productionItem.isReprint = false;

    this.addProduction(this.productionItem);
  }

  addProduction(data: any) {
    if (this.quantity > 10) {
      this.toastr.process('Processing...');
    }
    if (data.price) {
      const productionforDt = localStorage.getItem('WeekDate');
      const futproductionCycle = localStorage.getItem('FutureProductionCycle');
      const LocationID = localStorage.getItem('CurrentLocationID');

      if (futproductionCycle) {
        const parsedProductionCycle = JSON.parse(futproductionCycle);
        data.productionCycleScheduleID =
          parsedProductionCycle.productionCycleScheduleId;
        data.productionCycleID = parsedProductionCycle.productionCycleId;
      }

      if (LocationID) {
        const parsedLocationID = Number(JSON.parse(LocationID));
        data.currentLocationID =
          data.currentLocationID > 0
            ? data.currentLocationID
            : parsedLocationID;
      }

      data.productionforDt = productionforDt
        ? (() => {
            const now = new Date();
            const d = new Date(productionforDt + 'T00:00:00');
            d.setHours(
              now.getHours(),
              now.getMinutes(),
              now.getSeconds(),
              now.getMilliseconds()
            );
            return d.toISOString();
          })()
        : new Date().toISOString();

      const currentStoreId = JSON.parse(localStorage.getItem('storeId')!);
      data.currentStoreId = currentStoreId;
      const currentStationId = JSON.parse(localStorage.getItem('stationId')!);
      data.stationId = currentStationId;
      const metadata = JSON.parse(localStorage.getItem('productionMetadata')!);

      data.lotId = metadata?.lotId ?? 0;
      const selectedWarehouseLocationId = localStorage.getItem(
        'selectedWarehouseLocationId'
      );
      if (
        selectedWarehouseLocationId &&
        Number(selectedWarehouseLocationId) > 0
      ) {
        data.currentLocationID = Number(selectedWarehouseLocationId);
      } else {
        data.currentLocationID =
          !data.currentLocationID || Number(data.currentLocationID) === 0
            ? metadata.salesFloorLocationId
            : data.currentLocationID;
      }
      data.licensePlateId = this.overrideDetails?.licensePlateId || 0;

      this.masterService.addProductionWorking(data).subscribe(
        (res: any) => {
          if (this.isOverride) {
            try {
              const lpId = data.licensePlateId;
              const addedQty = Number(this.quantity) || 0;
              if (lpId) {
                const key = `licensePlateCount_${lpId}`;
                const prev =
                  parseInt(localStorage.getItem(key) || '0', 10) || 0;
                const updated = prev + addedQty;
                localStorage.setItem(key, updated.toString());
                localStorage.setItem('licensePlateCount', updated.toString());
                window.dispatchEvent(
                  new CustomEvent('licensePlateCountUpdated', {
                    detail: { licensePlateId: lpId, count: updated }
                  })
                );
              }
            } catch (e) {}
          }

          this.showSuccess();
          this.breadcrumbService.increment(this.quantity);
          let barcodes = res.map((item: any) => item.barcode).join(',');
          let userdata = this.masterService.getUserDetailsFromStorage();
          if (userdata.productionAutoPrint) {
            this.handleProductionSuccess(barcodes);
          }
        },
        (err) => {
          this.showError();
        }
      );
    } else {
      this.showPriceError();
    }
  }

  async handleProductionSuccess(barcodes: string) {
    const user = this.masterService.getUserDetailsFromStorage();
    const userID = user.userId;
    let currentStoreId = JSON.parse(localStorage.getItem('storeId')!);

    try {
      const res = await this.masterService.printLastLabel(barcodes).toPromise();

      this.printHistory = res as ProductionHistory[];

      const allPrintData: any[] = [];
      this.printHistory.forEach((item, index) => {
        item.brandDescr = '';

        for (let i = 0; i < this.pieces; i++) {
          const zplData = this.generateZpl(
            user.company,
            this.ProductInfo.department || '',
            this.ProductInfo.category || '',
            this.ProductInfo.subcategory || '',
            'Size:',
            item.sizeDescr,
            item.price.toFixed(2).toString(),
            item.barcode,
            currentStoreId,
            this.ProductInfo.qualityId,
            (i + 1).toString(),
            item.numberOfPieces.toString(),
            this.ProductInfo.brand || item.brandDescr
          );
          allPrintData.push(...zplData);
        }
      });

      if (allPrintData.length > 0) {
        await this.printLabelBatch(allPrintData);
      }
    } catch (error) {
      console.error(error);
    }
  }

  async printLabelBatch(printDataArray: any[]): Promise<void> {
    let printerName: string = '';

    if (this.printerType == 1) {
      printerName = 'ZebraLabel';
    } else if (this.printerType == 2) {
      printerName = 'ZebraTag';
    } else if (this.printerType == 3) {
      printerName = 'ZebraBoth';
    }

    try {
      await this.printService.printDataBatch(printerName, printDataArray);
      console.log(`Batch printed ${printDataArray.length} labels successfully`);
    } catch (error) {
      console.error('Batch printing failed:', error);
      for (const printData of printDataArray) {
        try {
          await this.printService.printData(printerName, [printData]);
        } catch (individualError) {
          console.error('Individual print failed:', individualError);
        }
      }
    }
  }

  generateZpl(
    CompanyName: string,
    Department: string,
    Category: string,
    SubCategory: string,
    SizeText: string = 'Size',
    SizeSize: string,
    Price: string,
    BarcodeValue: string,
    CompanyNameLine2: string,
    QualityID: number = 0,
    LabelIndex: string,
    Piece: string,
    Brand: string = ''
  ) {
    if (SizeSize == null) {
      SizeText = ' ';
    }
    let qualityDescr = this.ProductInfo.qualityDescr || '';

    if (Department == Category) {
      Category = ' ';
    }
    function safeParse(value: string | null): string {
      try {
        return value ? JSON.parse(value) : '';
      } catch {
        return value ?? '';
      }
    }

    let storeRaw =
      localStorage.getItem('storeName') ??
      localStorage.getItem('defaultStoreName') ??
      '';
    let Store: string = safeParse(storeRaw);

    let colors = JSON.parse(localStorage.getItem('productionCycle') || '""');
    let Color = colors[0].productionDisplayname;
    let WeekNumber = colors[0].weekNumber;

    if (
      Brand == null ||
      Brand == undefined ||
      Brand == 'None' ||
      Brand == 'Select Brand'
    ) {
      Brand = '';
    }

    let Date = '-';
    let Date2 = '-';
    const currentDate = moment();
    Date = currentDate.format('MM/DD');
    Date2 = currentDate.format('MM/DD/YYYY');
    const Time = currentDate.format('HH:mm');

    let userStorageData = this.masterService.getUserDetailsFromStorage();
    const User = userStorageData?.brijjusername || '';
    const DisplayName = userStorageData?.displayName || '';

    if (typeof this.previewZplCode !== 'string') {
      console.error('Invalid or missing ZPL code');
      return [];
    }
    this.formatLabels.map((res) => {
      if (res.id == this.ProductInfo.labelId) {
        this.previewZplCode = res.zplCode;
      }
    });
    const replacedZplCode = this.previewZplCode
      .replace(/\${CompanyName}/g, CompanyName ? CompanyName : '')
      .replace(/\${Color}/g, Color ? Color : '')
      .replace(/\${WeekNumber}/g, WeekNumber ? WeekNumber : '')
      .replace(/\${Department}/g, Department ? Department : '')
      .replace(/\${Category}/g, Category ? Category : '')
      .replace(/\${SubCategory}/g, SubCategory ? SubCategory : '')
      .replace(/\${SizeText}/g, SizeText ? SizeText : '')
      .replace(/\${SizeSize}/g, SizeSize ? SizeSize : '')
      .replace(/\${Price}/g, Price ? Price : '')
      .replace(/\${Date}/g, Date ? Date : '')
      .replace(/\${Date2}/g, Date2 ? Date2 : '')
      .replace(/\${Time}/g, Time)
      .replace(/\${User}/g, User)
      .replace(/\${Name}/g, DisplayName)
      .replace(/\${DisplayName}/g, DisplayName)
      .replace(/\${BarcodeValue}/g, BarcodeValue ? BarcodeValue : '')
      .replace(/\${Store}/g, Store ? Store : '')
      .replace(/\${Quality}/g, qualityDescr ? qualityDescr : '')
      .replace(/\${LabelIndex}/g, LabelIndex ? LabelIndex : '')
      .replace(/\${Piece}/g, Piece ? Piece : '')
      .replace(/\${Brand}/g, Brand ? Brand : '')
      .replace(
        /\${CompanyNameLine2}/g,
        CompanyNameLine2 ? CompanyNameLine2 : ''
      );

    const zplTemplate: string[] = replacedZplCode
      .split('\n')
      .map((line: string) => line.trim());
    return zplTemplate;
  }

  showSuccess() {
    this.toastr.success('New Price has been successfully added.');
  }

  showReprint() {
    this.toastr.success('Printing');
  }

  showDeleted() {
    this.toastr.success('Last item entry has been successfully deleted.');
  }

  showError() {
    this.toastr.error('Price Not Added');
  }

  showPriceError() {
    this.toastr.error('Price Not Selected');
  }

  toggleEdit(msg: ChatMessage): void {
    msg.editing = !msg.editing;
    if (msg.editing) {
      if (!msg.product) {
        msg.product = {
          department: '',
          category: '',
          price: '',
          qualityDescr: '',
          size: '',
          subcategory: ''
        };
      }

      if (msg.product) {
        msg.editBackup = JSON.parse(JSON.stringify(msg.product));
      }

      // Don't initialize search states - let them remain undefined
      // so the input value getters will show the actual selected values
    } else {
      delete msg.editBackup;
      delete msg.categorySearch;
    }
  }

  cancelEdit(msg: ChatMessage) {
    if (msg.editBackup) {
      msg.product = JSON.parse(JSON.stringify(msg.editBackup));
    }
    msg.editing = false;
    msg.editBackup = undefined;
    msg.categorySearch = undefined;
  }

  onDepartmentChange(msg: ChatMessage, ev: Event) {
    const val = (ev.target as HTMLSelectElement | null)?.value ?? '';
    if (!msg.product) msg.product = {};
    msg.product.department = val;
    msg.product.category = '';
    msg.product.subcategory = '';
    msg.product.size = '';
    msg.product.price = '';
    msg.product.brand = '';
    msg.product.qualityDescr = '';
    msg.categorySearch = undefined;
    msg.categoryDropdownOpen = false;
    msg.qualitySearch = undefined;
    msg.qualityDropdownOpen = false;

    // Find departmentId and fetch brands
    const department = this.hierarchicalData.find(
      (d) => d.departmentName === val
    );
    if (department) {
      msg.product.departmentId = department.departmentId;
      this.getBrands(department.departmentId);
    }
  }

  onCategoryChange(msg: ChatMessage, ev: Event) {
    const val = (ev.target as HTMLSelectElement | null)?.value ?? '';
    if (!msg.product) msg.product = {};
    msg.product.category = val;
    msg.product.subcategory = '';
    msg.product.size = '';
    msg.product.price = '';
    msg.product.brand = '';
    msg.product.qualityDescr = '';
    msg.qualitySearch = undefined;
    msg.qualityDropdownOpen = false;

    // Fetch brands based on departmentId if available
    if (msg.product.departmentId) {
      this.getBrands(msg.product.departmentId);
    }
  }

  onCategorySearchInput(msg: ChatMessage, event: Event) {
    const value = (event.target as HTMLInputElement).value;
    msg.categorySearch = value;
    msg.categoryDropdownOpen = true;

    // If search is completely cleared, reset the search state
    if (value.trim() === '') {
      msg.categorySearch = undefined;
    }
  }

  getCategoriesForDepartment(dept: string): DropdownOption[] {
    return this.dropdownData.categories[dept] || [];
  }

  private includeCurrentOption(
    options: DropdownOption[],
    current?: string | null
  ): DropdownOption[] {
    const cur = (current || '').trim();
    if (!cur) return options;
    const exists = options.some(
      (o) =>
        o.value.toLowerCase() === cur.toLowerCase() ||
        o.label.toLowerCase() === cur.toLowerCase()
    );
    return exists ? options : [{ value: cur, label: cur }, ...options];
  }

  getFilteredCategories(msg: ChatMessage): DropdownOption[] {
    const dept = msg.product?.department || '';
    const base = this.getCategoriesForDepartment(dept);
    const q = (msg.categorySearch || '').trim().toLowerCase();
    if (!q) return base;
    return base.filter(
      (c) =>
        c.label.toLowerCase().includes(q) || c.value.toLowerCase().includes(q)
    );
  }

  getDepartmentOptionsForEdit(msg: ChatMessage): DropdownOption[] {
    return this.includeCurrentOption(
      this.dropdownData.departments,
      msg.product?.department || ''
    );
  }

  getCategoryOptionsForEdit(msg: ChatMessage): DropdownOption[] {
    const filtered = this.getFilteredCategories(msg);
    return this.includeCurrentOption(filtered, msg.product?.category || '');
  }

  getQualityOptionsForEdit(msg: ChatMessage): DropdownOption[] {
    return this.includeCurrentOption(
      this.qualityOptions,
      msg.product?.qualityDescr || ''
    );
  }

  getSizeOptionsForEdit(msg: ChatMessage): DropdownOption[] {
    const dept = msg.product?.department || '';
    const cat = msg.product?.category || '';
    const sizes = this.sizeOptions[dept]?.[cat] || [];
    return this.includeCurrentOption(sizes, msg.product?.size || '');
  }

  priceOptionsByDepartmentCategory: {
    [department: string]: {
      [category: string]: DropdownOption[];
    };
  } = {};

  getPriceOptionsForEdit(msg: ChatMessage): DropdownOption[] {
    const priceOpts = this.priceOptionsByDepartmentCategory || {};
    const dept = msg.product?.department || '';
    const cat = msg.product?.category || '';

    const prices: DropdownOption[] = priceOpts[dept]?.[cat] || [];

    const currentPrice = (msg.product?.price || '').toString();
    const exists = prices.some((p: DropdownOption) => p.value === currentPrice);

    if (currentPrice && !exists) {
      return [{ value: currentPrice, label: currentPrice }, ...prices];
    }

    return prices;
  }

  saveEdit(msg: ChatMessage) {
    msg.editing = false;
    msg.editBackup = undefined;
    msg.categorySearch = undefined;
    // Clear all dropdown states
    msg.departmentDropdownOpen = false;
    msg.categoryDropdownOpen = false;
    msg.qualityDropdownOpen = false;
    msg.sizeDropdownOpen = false;
    msg.labelDropdownOpen = false;
    msg.brandDropdownOpen = false;
    msg.departmentSearch = undefined;
    msg.qualitySearch = undefined;
    msg.sizeSearch = undefined;
    msg.labelSearch = undefined;
    msg.brandSearch = undefined;
  }

  // Searchable Dropdown Methods

  // Input Value Getters (handle reset to selected value when search is cleared)
  getDepartmentInputValue(msg: ChatMessage): string {
    if (msg.departmentSearch !== undefined) {
      return msg.departmentSearch;
    }

    // If we have a department value, try to find its label from the available options
    const departmentValue = msg.product?.department || '';
    if (departmentValue) {
      const matchingDepartment = this.dropdownData.departments.find(
        (dept) =>
          dept.value === departmentValue || dept.label === departmentValue
      );
      return matchingDepartment ? matchingDepartment.label : departmentValue;
    }

    return departmentValue;
  }

  getCategoryInputValue(msg: ChatMessage): string {
    if (msg.categorySearch !== undefined) {
      return msg.categorySearch;
    }

    // If we have a category value, try to find its label from the available options
    const categoryValue = msg.product?.category || '';
    if (categoryValue && msg.product?.department) {
      const categories = this.getCategoriesForDepartment(
        msg.product.department
      );
      const matchingCategory = categories.find(
        (cat) => cat.value === categoryValue || cat.label === categoryValue
      );
      return matchingCategory ? matchingCategory.label : categoryValue;
    }

    return categoryValue;
  }

  getQualityInputValue(msg: ChatMessage): string {
    return msg.qualitySearch !== undefined
      ? msg.qualitySearch
      : msg.product?.qualityDescr || '';
  }

  getSizeInputValue(msg: ChatMessage): string {
    return msg.sizeSearch !== undefined
      ? msg.sizeSearch
      : msg.product?.size || '';
  }

  // Department Dropdown Methods
  toggleDepartmentDropdown(msg: ChatMessage) {
    msg.departmentDropdownOpen = !msg.departmentDropdownOpen;
    if (!msg.departmentDropdownOpen) {
      msg.departmentSearch = undefined;
    }
  }

  openDepartmentDropdown(msg: ChatMessage) {
    msg.departmentDropdownOpen = true;
  }

  closeDepartmentDropdown(msg: ChatMessage) {
    setTimeout(() => {
      msg.departmentDropdownOpen = false;
    }, 150);
  }

  onDepartmentSearchInput(msg: ChatMessage, event: Event) {
    const value = (event.target as HTMLInputElement).value;
    msg.departmentSearch = value;
    msg.departmentDropdownOpen = true;

    // If search is completely cleared, reset the search state
    if (value.trim() === '') {
      msg.departmentSearch = undefined;
    }
  }

  getFilteredDepartments(msg: ChatMessage): DropdownOption[] {
    const search = (msg.departmentSearch || '').toLowerCase().trim();
    const departments = this.getDepartmentOptionsForEdit(msg);

    if (!search) {
      return departments;
    }

    return departments.filter(
      (dept) =>
        dept.label.toLowerCase().includes(search) ||
        dept.value.toLowerCase().includes(search)
    );
  }

  isDepartmentSelected(msg: ChatMessage, dept: DropdownOption): boolean {
    const currentDepartment = msg.product?.department || '';
    return currentDepartment === dept.value || currentDepartment === dept.label;
  }

  selectDepartment(msg: ChatMessage, dept: DropdownOption) {
    if (!msg.product) msg.product = {};
    msg.product.department = dept.value;
    msg.product.category = '';
    msg.product.subcategory = '';
    msg.product.size = '';
    msg.product.brand = '';
    msg.product.qualityDescr = '';
    msg.departmentSearch = undefined;
    msg.departmentDropdownOpen = false;
    msg.categorySearch = undefined;
    msg.categoryDropdownOpen = false;
    msg.qualitySearch = undefined;
    msg.qualityDropdownOpen = false;

    // Find departmentId and fetch brands
    const department = this.hierarchicalData.find(
      (d) => d.departmentName === dept.value
    );
    if (department) {
      msg.product.departmentId = department.departmentId;
      this.getBrands(department.departmentId);
    }
  }

  // Category Dropdown Methods
  toggleCategoryDropdown(msg: ChatMessage) {
    if (!msg.product?.department) return;
    msg.categoryDropdownOpen = !msg.categoryDropdownOpen;
    if (!msg.categoryDropdownOpen) {
      msg.categorySearch = undefined;
    }
  }

  openCategoryDropdown(msg: ChatMessage) {
    if (!msg.product?.department) return;
    msg.categoryDropdownOpen = true;
  }

  closeCategoryDropdown(msg: ChatMessage) {
    setTimeout(() => {
      msg.categoryDropdownOpen = false;
    }, 150);
  }

  isCategorySelected(msg: ChatMessage, cat: DropdownOption): boolean {
    const currentCategory = msg.product?.category || '';
    return currentCategory === cat.value || currentCategory === cat.label;
  }

  selectCategory(msg: ChatMessage, cat: DropdownOption) {
    if (!msg.product) msg.product = {};
    msg.product.category = cat.value;
    msg.product.subcategory = '';
    msg.product.size = '';
    msg.product.brand = '';
    msg.product.qualityDescr = '';

    // Find and set categoryId
    if (msg.product && msg.product.departmentId) {
      const department = this.hierarchicalData.find(
        (d) => d.departmentId === msg.product!.departmentId
      );
      if (department) {
        const category = department.categories.find(
          (c) => c.categoryName === cat.value
        );
        if (category && msg.product) {
          msg.product.categoryId = category.categoryId;
        }
      }
    }

    msg.categorySearch = undefined;
    msg.categoryDropdownOpen = false;
    msg.qualitySearch = undefined;
    msg.qualityDropdownOpen = false;
  }

  // Quality Dropdown Methods
  toggleQualityDropdown(msg: ChatMessage) {
    msg.qualityDropdownOpen = !msg.qualityDropdownOpen;
    if (!msg.qualityDropdownOpen) {
      msg.qualitySearch = undefined;
    }
  }

  openQualityDropdown(msg: ChatMessage) {
    msg.qualityDropdownOpen = true;
  }

  closeQualityDropdown(msg: ChatMessage) {
    setTimeout(() => {
      msg.qualityDropdownOpen = false;
    }, 150);
  }

  onQualitySearchInput(msg: ChatMessage, event: Event) {
    const value = (event.target as HTMLInputElement).value;
    msg.qualitySearch = value;
    msg.qualityDropdownOpen = true;

    // If search is completely cleared, reset the search state
    if (value.trim() === '') {
      msg.qualitySearch = undefined;
    }
  }

  getFilteredQualities(msg: ChatMessage): DropdownOption[] {
    const search = (msg.qualitySearch || '').toLowerCase().trim();
    const qualities = this.getQualityOptionsForEdit(msg);

    if (!search) {
      return qualities;
    }

    return qualities.filter(
      (quality) =>
        quality.label.toLowerCase().includes(search) ||
        quality.value.toLowerCase().includes(search)
    );
  }

  isQualitySelected(msg: ChatMessage, quality: DropdownOption): boolean {
    const currentQuality = msg.product?.qualityDescr || '';
    return currentQuality === quality.value || currentQuality === quality.label;
  }

  selectQuality(msg: ChatMessage, quality: DropdownOption) {
    if (!msg.product) msg.product = {};
    msg.product.qualityDescr = quality.value;
    msg.qualitySearch = undefined;
    msg.qualityDropdownOpen = false;
  }

  // Size Dropdown Methods
  toggleSizeDropdown(msg: ChatMessage) {
    msg.sizeDropdownOpen = !msg.sizeDropdownOpen;
    if (!msg.sizeDropdownOpen) {
      msg.sizeSearch = undefined;
    }
  }

  openSizeDropdown(msg: ChatMessage) {
    msg.sizeDropdownOpen = true;
  }

  closeSizeDropdown(msg: ChatMessage) {
    setTimeout(() => {
      msg.sizeDropdownOpen = false;
    }, 150);
  }

  onSizeSearchInput(msg: ChatMessage, event: Event) {
    const value = (event.target as HTMLInputElement).value;
    msg.sizeSearch = value;
    msg.sizeDropdownOpen = true;

    // If search is completely cleared, reset the search state
    if (value.trim() === '') {
      msg.sizeSearch = undefined;
    }
  }

  getFilteredSizes(msg: ChatMessage): DropdownOption[] {
    const search = (msg.sizeSearch || '').toLowerCase().trim();
    const sizes = this.getSizeOptionsForEdit(msg);

    if (!search) {
      return sizes;
    }

    return sizes.filter(
      (size) =>
        size.label.toLowerCase().includes(search) ||
        size.value.toLowerCase().includes(search)
    );
  }

  isSizeSelected(msg: ChatMessage, size: DropdownOption): boolean {
    const currentSize = msg.product?.size || '';
    return currentSize === size.value || currentSize === size.label;
  }

  selectSize(msg: ChatMessage, size: DropdownOption) {
    if (!msg.product) msg.product = {};
    msg.product.size = size.value;
    msg.sizeSearch = undefined;
    msg.sizeDropdownOpen = false;
  }

  // Brand Dropdown Methods
  getBrandInputValue(msg: ChatMessage): string {
    return msg.brandSearch !== undefined
      ? msg.brandSearch
      : msg.product?.brand || '';
  }

  toggleBrandDropdown(msg: ChatMessage) {
    msg.brandDropdownOpen = !msg.brandDropdownOpen;
    if (!msg.brandDropdownOpen) {
      msg.brandSearch = undefined;
    }
  }

  openBrandDropdown(msg: ChatMessage) {
    msg.brandDropdownOpen = true;
  }

  closeBrandDropdown(msg: ChatMessage) {
    setTimeout(() => {
      msg.brandDropdownOpen = false;
    }, 150);
  }

  onBrandSearchInput(msg: ChatMessage, event: Event) {
    const value = (event.target as HTMLInputElement).value;
    msg.brandSearch = value;
    msg.brandDropdownOpen = true;

    // If search is completely cleared, reset the search state
    if (value.trim() === '') {
      msg.brandSearch = undefined;
    }
  }

  getFilteredBrands(msg: ChatMessage): DropdownOption[] {
    const search = (msg.brandSearch || '').toLowerCase().trim();
    const brands = this.getBrandOptionsForEdit(msg);

    if (!search) {
      return brands;
    }

    return brands.filter(
      (brand) =>
        brand.label.toLowerCase().includes(search) ||
        brand.value.toLowerCase().includes(search)
    );
  }

  getBrandOptionsForEdit(msg: ChatMessage): DropdownOption[] {
    const brandOptions = this.brands.map((brand) => ({
      value: brand.brandDescr,
      label: brand.brandDescr
    }));
    return this.includeCurrentOption(brandOptions, msg.product?.brand || '');
  }

  isBrandSelected(msg: ChatMessage, brand: DropdownOption): boolean {
    const currentBrand = msg.product?.brand || '';
    return currentBrand === brand.value || currentBrand === brand.label;
  }

  selectBrand(msg: ChatMessage, brand: DropdownOption) {
    if (!msg.product) msg.product = {};
    msg.product.brand = brand.value;

    // Find and set brandId
    const selectedBrand = this.brands.find((b) => b.brandDescr === brand.value);
    if (selectedBrand) {
      msg.product.brandId = selectedBrand.brandId;

      // Get AI price when brand changes
      this.getAIPrice(msg, selectedBrand.brandId);
    }

    msg.brandSearch = undefined;
    msg.brandDropdownOpen = false;
  }

  getAIPrice(msg: ChatMessage, brandId: number) {
    if (!msg.product?.departmentId || !msg.product?.categoryId || !brandId) {
      console.log('Missing required IDs for AI price fetch:', {
        departmentId: msg.product?.departmentId,
        categoryId: msg.product?.categoryId,
        brandId: brandId
      });
      return;
    }

    this.masterService
      .getAIPrice(msg.product.departmentId, msg.product.categoryId, brandId)
      .subscribe({
        next: (res: any) => {
          if (res && res.length > 0) {
            const aiPrice = res[0];
            let newPrice = '';

            if (aiPrice.ebayPrice && aiPrice.ebayPrice > 0) {
              newPrice = aiPrice.ebayPrice.toFixed(2);
            } else if (aiPrice.poshmarkPrice && aiPrice.poshmarkPrice > 0) {
              newPrice = aiPrice.poshmarkPrice.toFixed(2);
            }

            // Update price if we got a valid price from AI
            if (newPrice && parseFloat(newPrice) > 0) {
              if (msg.product) {
                msg.product.price = newPrice;
              }
              this.addBotMessage(
                `Price updated to $${newPrice} based on AI pricing for selected brand.`
              );
            } else {
              this.addBotMessage(
                'No AI pricing available for this brand. Current price maintained.'
              );
            }
          } else {
            this.addBotMessage(
              'No AI pricing data found for this brand. Current price maintained.'
            );
          }
        },
        error: (error) => {
          console.error('Error fetching AI price:', error);
          this.addBotMessage(
            'Failed to fetch AI pricing. Current price maintained.'
          );
        }
      });
  }

  // Quantity and Pieces Input Handlers
  onQuantityInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.quantity = parseFloat(value) || 1;
  }

  onPiecesInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.pieces = parseFloat(value) || 1;
  }

  // Label Dropdown Methods
  getLabelInputValue(msg: ChatMessage): string {
    return msg.labelSearch !== undefined
      ? msg.labelSearch
      : this.getSelectedLabelName(msg);
  }

  getSelectedLabelName(msg: ChatMessage): string {
    if (this.ProductInfo.labelId) {
      const selectedLabel = this.formatLabels.find(
        (label) => label.id === this.ProductInfo.labelId
      );
      return selectedLabel ? selectedLabel.name : '';
    }
    return '';
  }

  toggleLabelDropdown(msg: ChatMessage) {
    msg.labelDropdownOpen = !msg.labelDropdownOpen;
    if (!msg.labelDropdownOpen) {
      msg.labelSearch = undefined;
    }
  }

  openLabelDropdown(msg: ChatMessage) {
    msg.labelDropdownOpen = true;
  }

  closeLabelDropdown(msg: ChatMessage) {
    setTimeout(() => {
      msg.labelDropdownOpen = false;
    }, 150);
  }

  onLabelSearchInput(msg: ChatMessage, event: Event) {
    const value = (event.target as HTMLInputElement).value;
    msg.labelSearch = value;
    msg.labelDropdownOpen = true;

    // If search is completely cleared, reset the search state
    if (value.trim() === '') {
      msg.labelSearch = undefined;
    }
  }

  getFilteredLabels(msg: ChatMessage): Labels[] {
    const search = (msg.labelSearch || '').toLowerCase().trim();

    if (!search) {
      return this.formatLabels;
    }

    return this.formatLabels.filter(
      (label) =>
        label.name.toLowerCase().includes(search) ||
        label.id.toString().includes(search)
    );
  }

  selectLabel(msg: ChatMessage, label: Labels) {
    this.ProductInfo.labelId = label.id;
    this.printerType = label.printerType;
    msg.labelSearch = undefined;
    msg.labelDropdownOpen = false;
  }

  onProductFieldInput(msg: ChatMessage, key: keyof ProductInfo, ev: Event) {
    const val = (ev.target as HTMLInputElement | null)?.value ?? '';
    if (!msg.product) msg.product = {};
    (msg.product as any)[key] = val;

    // Check if price was updated
    if (key === 'price' && val) {
      this.checkPriceQualityMatch(msg, val);
    }
  }

  // 3. Add this new method to check if the price matches any quality option
  private checkPriceQualityMatch(msg: ChatMessage, priceValue: string): void {
    if (!msg.product?.department || !msg.product?.category) {
      return;
    }

    const dept = msg.product.department;
    const cat = msg.product.category;
    const sub = msg.product.subcategory || 'None';

    // Get available prices for the current department/category/subcategory
    const availablePrices = this.priceOptions[dept]?.[cat]?.[sub] || [];

    // Clean the input price value
    const cleanPrice = priceValue.replace(/\$/g, '').trim();
    const numericPrice = parseFloat(cleanPrice);

    if (isNaN(numericPrice)) {
      return;
    }

    // Find matching price configurations
    const matchingPrices = availablePrices.filter((priceOption) => {
      const optionPrice = parseFloat(priceOption.value);
      return Math.abs(optionPrice - numericPrice) < 0.01; // Allow for floating point precision
    });

    if (matchingPrices.length > 0) {
      // Found matching price(s) with quality options
      if (matchingPrices.length === 1) {
        const matchedOption = matchingPrices[0];
        // Extract quality from label (format: "$19.99 (Like New)")
        const qualityMatch = matchedOption.label.match(/\(([^)]+)\)/);
        const suggestedQuality = qualityMatch ? qualityMatch[1] : '';

        if (suggestedQuality && suggestedQuality !== msg.product.qualityDescr) {
          // Add bot message with quality suggestion and action buttons
          this.addBotMessageWithQualityAction(
            `The price $${numericPrice.toFixed(
              2
            )} matches the quality "${suggestedQuality}".`,
            msg,
            suggestedQuality
          );
        }
      } else {
        // Multiple quality options available for this price
        const qualityOptions = matchingPrices
          .map((p) => {
            const qualityMatch = p.label.match(/\(([^)]+)\)/);
            return qualityMatch ? qualityMatch[1] : '';
          })
          .filter((q) => q !== '');

        if (qualityOptions.length > 0) {
          // Add bot message with multiple quality options
          this.addBotMessageWithMultipleQualityActions(
            ` The price $${numericPrice.toFixed(
              2
            )} is available for multiple quality options:`,
            msg,
            qualityOptions
          );
        }
      }
    } else {
      // No exact match found - check if price is close to any available option
      const closePrices = availablePrices.filter((priceOption) => {
        const optionPrice = parseFloat(priceOption.value);
        const difference = Math.abs(optionPrice - numericPrice);
        return difference > 0.01 && difference < 2.0; // Within $2
      });

      if (closePrices.length > 0) {
        const suggestions = closePrices
          .slice(0, 3) // Show max 3 suggestions
          .map((p) => p.label)
          .join(', ');

        this.addBotMessage(
          `No exact match found for price $${numericPrice.toFixed(2)}. ` +
            `Similar options available: ${suggestions}`
        );
      }
    }
  }

  // 4. Add method to create bot message with single quality action
  private addBotMessageWithQualityAction(
    text: string,
    targetMsg: ChatMessage,
    quality: string
  ): void {
    setTimeout(() => {
      this.chatMessages.push({
        sender: 'bot',
        text: text,
        qualitySuggestion: {
          targetMessage: targetMsg,
          suggestedQuality: quality
        }
      });
    }, 300);
  }

  // 5. Add method to create bot message with multiple quality options
  private addBotMessageWithMultipleQualityActions(
    text: string,
    targetMsg: ChatMessage,
    qualities: string[]
  ): void {
    setTimeout(() => {
      this.chatMessages.push({
        sender: 'bot',
        text: text,
        qualitySuggestions: {
          targetMessage: targetMsg,
          suggestedQualities: qualities
        }
      });
    }, 300);
  }

  // 6. Add method to accept quality suggestion
  acceptQualitySuggestion(
    msg: ChatMessage,
    targetMsg: ChatMessage,
    quality: string
  ): void {
    if (!targetMsg.product) {
      targetMsg.product = {};
    }

    targetMsg.product.qualityDescr = quality;

    // Find qualityId if needed
    const qualityOption = this.qualityOptions.find((q) => q.value === quality);
    if (qualityOption) {
      // You may need to set qualityId based on your data structure
      this.addBotMessage(`✅ Quality updated to "${quality}".`);
    }

    // Mark this message as handled
    msg.qualitySuggestion = undefined;
    msg.qualitySuggestions = undefined;
  }

  // 7. Add method to accept specific quality from multiple options
  acceptSpecificQuality(
    msg: ChatMessage,
    targetMsg: ChatMessage,
    quality: string
  ): void {
    this.acceptQualitySuggestion(msg, targetMsg, quality);
  }

  // 8. Add method to decline quality suggestion
  declineQualitySuggestion(msg: ChatMessage): void {
    this.addBotMessage('Okay, quality will remain unchanged.');
    msg.qualitySuggestion = undefined;
    msg.qualitySuggestions = undefined;
  }

  ngOnDestroy(): void {
    if (this.playbackUrl) URL.revokeObjectURL(this.playbackUrl);
    if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
  }
}
